import { useState, useEffect, useMemo, useCallback } from 'react';
import { useApp } from '../../context';
import api from '../../hooks/useApi';
import useRefreshOnFocus from '../../hooks/useRefreshOnFocus';
import { ActionCard } from './MyActionsPage';
import Avatar from '../shared/Avatar';

// ── Action Oversight ─────────────────────────────────────────────────────────
// Org-wide view of the person-to-person work queue for managers/PMO: who raised
// what, who it's on, how far through, and what's overdue or stalled. Mirrors the
// delivery-side Task Oversight, but for actions.

const today = () => new Date().toISOString().split('T')[0];

function initials(name) {
  if (!name) return '?';
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map(p => p[0]).join('').toUpperCase();
}

function Kpi({ label, value, color, active, onClick }) {
  return (
    <button onClick={onClick}
      className={`bg-white border rounded p-2 text-left transition-all ${active ? 'border-navy ring-1 ring-navy/20' : 'border-gray-200 hover:border-gray-300'}`}>
      <div className="text-[8px] text-gray-500 uppercase font-bold">{label}</div>
      <div className={`text-base font-extrabold ${color}`}>{value}</div>
    </button>
  );
}

export default function ActionOversightPage() {
  const { currentUser } = useApp();
  const [actions, setActions] = useState(null);
  const [people, setPeople]   = useState([]);
  const [status, setStatus]   = useState('open');
  const [groupBy, setGroupBy] = useState('assignee'); // 'assignee' | 'raiser'
  const [search, setSearch]   = useState('');
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [loadError, setLoadError] = useState(null);

  const load = useCallback(() => {
    api.get('actions/overview', { status })
      .then(a => { setActions(Array.isArray(a) ? a : []); setLoadError(null); })
      .catch(e => { setActions([]); setLoadError(e?.message || 'Could not load actions.'); });
  }, [status]);
  useEffect(() => { load(); }, [load]);
  useRefreshOnFocus(load);
  useEffect(() => { api.get('people').then(p => setPeople(Array.isArray(p) ? p : [])).catch(() => {}); }, []);

  const isOverdue = (a) => a.status === 'open' && a.due_date && a.due_date < today();
  // Stalled: open, no activity logged for 14+ days.
  const isStalled = (a) => {
    if (a.status !== 'open' || !a.updated_at) return false;
    return (Date.now() - new Date(a.updated_at.replace(' ', 'T') + 'Z').getTime()) > 14 * 86400000;
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (actions || []).filter(a => {
      if (q && !(`${a.title} ${a.created_by_name || ''} ${a.sow_reference || ''} ${(a.assignees || []).map(x => x.person_name).join(' ')}`.toLowerCase().includes(q))) return false;
      if (overdueOnly && !isOverdue(a)) return false;
      return true;
    });
  }, [actions, search, overdueOnly]);

  const stats = useMemo(() => ({
    total:    filtered.length,
    open:     filtered.filter(a => a.status === 'open').length,
    overdue:  filtered.filter(isOverdue).length,
    stalled:  filtered.filter(isStalled).length,
  }), [filtered]);

  // Group by the person it's on (each assignee gets a row) or by who raised it.
  const groups = useMemo(() => {
    const byKey = new Map();
    const push = (key, label, a) => {
      if (!byKey.has(key)) byKey.set(key, { key, label, actions: [] });
      byKey.get(key).actions.push(a);
    };
    for (const a of filtered) {
      if (groupBy === 'raiser') {
        push(a.created_by || '__none__', a.created_by_name || 'Unknown', a);
      } else {
        const assignees = a.assignees || [];
        if (assignees.length === 0) push('__unassigned__', 'Unassigned', a);
        else assignees.forEach(p => push(p.person_id, p.person_name, a));
      }
    }
    return [...byKey.values()].map(g => ({
      ...g,
      overdue: g.actions.filter(isOverdue).length,
    })).sort((a, b) => {
      if (b.overdue !== a.overdue) return b.overdue - a.overdue;
      return b.actions.length - a.actions.length;
    });
  }, [filtered, groupBy]);

  const toggle = (k) => setExpanded(p => ({ ...p, [k]: !p[k] }));

  if (actions === null) return <div className="p-6 text-[10px] text-gray-400">Loading action oversight…</div>;

  return (
    <div className="fade-in">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <h2 className="text-base font-extrabold text-navy">Action Oversight</h2>
          <div className="text-[9px] text-gray-500">Every live action across the team — who raised it, who it's on, and which are overdue or stalling. Nothing asked of someone should quietly fall through.</div>
        </div>
        <button onClick={load} className="px-3 py-1.5 border border-gray-200 text-[10px] font-semibold rounded hover:bg-gray-50">↻ Refresh</button>
      </div>

      <div className="grid grid-cols-4 gap-2 mb-3">
        <Kpi label="Showing" value={stats.total} color="text-navy" active={!overdueOnly} onClick={() => setOverdueOnly(false)} />
        <Kpi label="Open" value={stats.open} color="text-amber-500" active={status === 'open'} onClick={() => setStatus('open')} />
        <Kpi label="Overdue" value={stats.overdue} color="text-red-500" active={overdueOnly} onClick={() => setOverdueOnly(v => !v)} />
        <Kpi label="Stalled 14d+" value={stats.stalled} color="text-orange-500" active={false} onClick={() => {}} />
      </div>

      <div className="flex flex-wrap gap-2 items-center mb-3">
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search action, person, SOW…"
          className="text-[10px] border border-gray-200 rounded px-2.5 py-1.5 focus:outline-none focus:border-navy w-56" />
        <div className="flex border border-gray-200 rounded overflow-hidden">
          {[{ id: 'open', l: 'Open' }, { id: 'resolved', l: 'Historical' }, { id: 'all', l: 'All' }].map(s => (
            <button key={s.id} onClick={() => setStatus(s.id)}
              className={`px-2 py-1 text-[9px] font-semibold ${status === s.id ? 'bg-gray-700 text-white' : 'bg-white text-gray-600'}`}>{s.l}</button>
          ))}
        </div>
        <label className="flex items-center gap-1 text-[9px] text-gray-600 cursor-pointer">
          <input type="checkbox" checked={overdueOnly} onChange={e => setOverdueOnly(e.target.checked)} /> Overdue only
        </label>
        <div className="ml-auto flex items-center gap-1 text-[9px]">
          <span className="text-gray-400">Group by</span>
          <div className="flex border border-gray-200 rounded overflow-hidden">
            <button onClick={() => setGroupBy('assignee')} className={`px-2 py-1 font-semibold ${groupBy === 'assignee' ? 'bg-navy text-white' : 'bg-white text-gray-600'}`}>Person it's on</button>
            <button onClick={() => setGroupBy('raiser')} className={`px-2 py-1 font-semibold ${groupBy === 'raiser' ? 'bg-navy text-white' : 'bg-white text-gray-600'}`}>Raiser</button>
          </div>
        </div>
      </div>

      {loadError ? (
        <div className="text-center text-[10px] text-red-700 bg-red-50 py-10 border border-red-200 rounded">
          <div className="font-semibold">Couldn't load the actions. Use Refresh to try again.</div>
          <div className="mt-1 text-[9px] text-red-600">{loadError}</div>
        </div>
      ) : groups.length === 0 ? (
        <div className="text-center text-[10px] text-gray-400 py-10 border border-dashed border-gray-200 rounded">No actions match these filters.</div>
      ) : (
        <div className="space-y-1.5">
          {groups.map(g => {
            const isOpen = !!expanded[g.key];
            return (
              <div key={g.key} className="border border-gray-200 rounded bg-white">
                <button onClick={() => toggle(g.key)} className="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-50 text-left">
                  <span className="text-gray-300 text-[10px] w-3">{isOpen ? '▾' : '▸'}</span>
                  <Avatar as="span" personId={g.key} className="w-6 h-6 rounded-full flex items-center justify-center text-[8px] font-bold text-white shrink-0 bg-navy">{initials(g.label)}</Avatar>
                  <span className="flex-1 text-[11px] font-bold text-gray-800 truncate">{g.label}</span>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <span className="text-[8px] bg-gray-100 text-gray-600 font-bold px-1.5 py-0.5 rounded">{g.actions.length}</span>
                    {g.overdue > 0 && <span className="text-[8px] bg-red-100 text-red-600 font-bold px-1.5 py-0.5 rounded">{g.overdue} overdue</span>}
                  </div>
                </button>
                {isOpen && (
                  <div className="border-t border-gray-100 p-2 space-y-1.5">
                    {g.actions.map(a => (
                      <ActionCard key={`${g.key}-${a.id}`} action={a} currentUser={currentUser} people={people}
                        onChanged={load} showClear={false} answerForOthers />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
